import React, { useState, useMemo } from 'react';
import useTransactions from '../../hooks/useTransactions';
import DashboardSummary from '../SummaryCard';
import TransactionTable from '../TransactionTable';

const Dashboard: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusPaid, setStatusPaid] = useState('');
  const [status, setStatus] = useState('');

  const { data, loading, error, domains } = useTransactions(domain, startDate, endDate, statusPaid, status);

  // เพิ่ม console.log เพื่อตรวจสอบค่า domains
  console.log('Domains:', domains);

  const handleDomainChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setDomain(event.target.value);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleStatusPaidChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusPaid(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.target.value);
  };

  const {
    totalPayAmount,
    successfulTotalPayAmount,
    unsuccessfulTotalPayAmount,
    totalNetAmount,
    successfulTotalNetAmount,
    unsuccessfulTotalNetAmount,
    totalFee,
    successfulTotalFee,
    unsuccessfulTotalFee,
    totalUnpaidAmount,
    totalPaidAmount
  } = useMemo(() => {
    if (!data) return {
      totalPayAmount: 0,
      successfulTotalPayAmount: 0,
      unsuccessfulTotalPayAmount: 0,
      totalNetAmount: 0,
      successfulTotalNetAmount: 0,
      unsuccessfulTotalNetAmount: 0,
      totalFee: 0,
      successfulTotalFee: 0,
      unsuccessfulTotalFee: 0,
      totalUnpaidAmount: 0,
      totalPaidAmount: 0
    };

    const allTransactions = data.data.transactions;
    const successfulTransactions = allTransactions.filter(t => t.status === 1);
    const unsuccessfulTransactions = allTransactions.filter(t => t.status === 0);

    const totalPayAmount = allTransactions.reduce((sum, t) => sum + t.amount, 0);
    const successfulTotalPayAmount = successfulTransactions.reduce((sum, t) => sum + t.amount, 0);
    const unsuccessfulTotalPayAmount = unsuccessfulTransactions.reduce((sum, t) => sum + t.amount, 0);

    const totalNetAmount = allTransactions.reduce((sum, t) => sum + (t.amount - t.fee), 0);
    const successfulTotalNetAmount = successfulTransactions.reduce((sum, t) => sum + (t.amount - t.fee), 0);
    const unsuccessfulTotalNetAmount = unsuccessfulTransactions.reduce((sum, t) => sum + (t.amount - t.fee), 0);

    const totalFee = allTransactions.reduce((sum, t) => sum + t.fee, 0);
    const successfulTotalFee = successfulTransactions.reduce((sum, t) => sum + t.fee, 0);
    const unsuccessfulTotalFee = unsuccessfulTransactions.reduce((sum, t) => sum + t.fee, 0);

    const totalUnpaidAmount = successfulTransactions
      .filter(t => !t.status_paid)
      .reduce((sum, t) => sum + t.amount, 0);

    const totalPaidAmount = successfulTransactions
      .filter(t => t.status_paid)
      .reduce((sum, t) => sum + t.amount, 0);

    return {
      totalPayAmount,
      successfulTotalPayAmount,
      unsuccessfulTotalPayAmount,
      totalNetAmount,
      successfulTotalNetAmount,
      unsuccessfulTotalNetAmount,
      totalFee,
      successfulTotalFee,
      unsuccessfulTotalFee,
      totalUnpaidAmount,
      totalPaidAmount
    };
  }, [data]);

  if (loading) return <div>กำลังโหลด...</div>;
  if (error) return <div>เกิดข้อผิดพลาด: {error}</div>;
  if (!data) return null;

  const summaryData = {
    totalPayAmount,
    successfulTotalPayAmount,
    unsuccessfulTotalPayAmount,
    totalNetAmount,
    successfulTotalNetAmount,
    unsuccessfulTotalNetAmount,
    totalFee,
    successfulTotalFee,
    unsuccessfulTotalFee,
    totalUnpaidAmount,
    totalPaidAmount,
    transactionCount: data.data.count
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">แดชบอร์ด NGXPAY</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">โดเมน</label>
          <select
            value={domain}
            onChange={handleDomainChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทุก Domain</option>
            {Array.isArray(domains) && domains.length > 0 ? (
              domains.map((d: string) => (
                <option key={d} value={d}>{d}</option>
              ))
            ) : (
              <option disabled>ไม่พบโดเมน</option>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">วันที่เริ่มต้น</label>
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">วันที่สิ้นสุด</label>
          <input
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">สถานะการเบิก</label>
          <select
            value={statusPaid}
            onChange={handleStatusPaidChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทั้งหมด</option>
            <option value="0">ยังไม่เบิก</option>
            <option value="1">เบิกแล้ว</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">สถานะการทำรายการ</label>
          <select
            value={status}
            onChange={handleStatusChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทั้งหมด</option>
            <option value="0">รอดำเนินการ</option>
            <option value="1">สำเร็จ</option>
          </select>
        </div>
      </div>
      <DashboardSummary data={summaryData} />
      <TransactionTable transactions={data.data.transactions} />
    </div>
  );
};

export default Dashboard;
